import { AuthState } from '@/types/auth'
import { UserType } from '@/types/user'
import { useAtom, useSetAtom } from 'jotai'
import { atomWithStorage, createJSONStorage } from 'jotai/utils'
import api from '@/lib/api'
import { AUTH_STORAGE_KEY, USER_SESSION_SNAPSHOT_KEY } from '@/features/auth/utils/auth-storage'

const initialState: AuthState = {
  user: null,
  token: null,
  client: null,
  permissions: null,
  isLoading: false
}

const authAtom = atomWithStorage<AuthState>(AUTH_STORAGE_KEY, initialState, createJSONStorage(), { getOnInit: true })

export const saveUserSessionSnapshot = (authState: AuthState | null | undefined) => {
  if (!authState?.user || !authState?.token) return

  localStorage.setItem(USER_SESSION_SNAPSHOT_KEY, JSON.stringify(authState))
}

export const restoreUserSessionSnapshot = (): AuthState | null => {
  const snapshot = localStorage.getItem(USER_SESSION_SNAPSHOT_KEY)
  if (!snapshot) return null

  try {
    const parsed = JSON.parse(snapshot) as AuthState
    localStorage.removeItem(USER_SESSION_SNAPSHOT_KEY)
    return parsed
  } catch (error) {
    console.error('Erro ao restaurar sessão do usuário:', error)
    localStorage.removeItem(USER_SESSION_SNAPSHOT_KEY)
    return null
  }
}

export const clearUserSessionSnapshot = () => {
  localStorage.removeItem(USER_SESSION_SNAPSHOT_KEY)
}

export const useAuthState = () => useAtom(authAtom)
export const useSetAuthState = () => useSetAtom(authAtom)

export const useClearAuthState = () => {
  const setAuthState = useSetAuthState()

  return () => {
    setAuthState(initialState)
    localStorage.removeItem(AUTH_STORAGE_KEY)
    clearUserSessionSnapshot()
  }
}

export const useAccessToken = () => {
  const [authState] = useAuthState()
  return authState.token
}

export const useCurrentUser = () => {
  const [authState] = useAuthState()
  return authState.user
}

export const useCurrentClient = () => {
  const [authState] = useAuthState()
  return authState.client
}

export const usePermissions = () => {
  const [authState] = useAuthState()
  return authState.permissions
}

export const useUpdateUser = () => {
  const [authState, setAuthState] = useAuthState()

  return (userData: Partial<UserType>) => {
    if (!authState.user) {
      console.error('Usuário não encontrado para atualizar')
      return
    }

    const newUser = { ...authState.user, ...userData }
    setAuthState({
      ...authState,
      user: newUser
    })
  }
}

import { useCallback } from 'react'

export const useLoadPermissions = () => {
  const [authState, setAuth] = useAuthState()

  return useCallback(
    async (acc_token: string) => {
      setAuth(prev => ({ ...prev, isLoading: true }))
      try {
        const { data } = await api.get(`/permissions/${authState.user?.id}`, {
          headers: { Authorization: `Bearer ${acc_token}` }
        })
        const permissionsData = data?.data
        setAuth(prev => ({
          ...prev,
          permissions: permissionsData,
          isLoading: false
        }))
      } catch (error) {
        console.error('Erro ao carregar permissões:', error)
        setAuth(prev => ({ ...prev, isLoading: false }))
        throw error
      }
    },
    [setAuth]
  )
}
